import { PayloadAction } from '@reduxjs/toolkit';
import {
  AssessmentEvaluationActions,
  AuthActions,
  AuthSelectors,
  ConfigActions,
  ConfigSelectors,
  ExperimentsActions,
  ExperimentsSelectors,
  FirebaseActionTypes,
  FirebaseActions,
  HistogramActions,
  IterableActions,
  IterableSelectors,
  LanguageActions,
  LanguageSelectors,
  ProgressActions,
  ProgressSelectors,
  RemoteConfigService,
  StatsActions,
  StatsSelectors,
  waitForExperimentsFeature,
  waitForFirebaseReadySaga,
  waitForFullProgressSaga,
} from '@web_opxp/features';
import { Lottie } from '@web_opxp/lottie';
import { all, call, fork, put, select, spawn, take, takeEvery, takeLatest, takeLeading } from 'redux-saga/effects';
import { ApiService, addAuthInterceptor, addRetry } from 'web_core_library';
import { AchievementsActions } from '../achievements';
import {
  DEFAULT_DAY0_BANNER,
  DEFAULT_WELCOME_LP,
  TRAINING_REMINDER,
  WORKOUT_PROGRESS_MIGRATION,
} from '../base/constants';
import ENV from '../base/env';
import * as HistoryService from '../base/historyService';
import {
  getCurrentSearchParams,
  isOnPage,
  openExternalUrl,
  redirectExternal,
  redirectInternal,
  redirectToCheckout,
  redirectToOnboarding,
  redirectToSubscribeWithCoupon,
} from '../base/historyService';
import { IDay0Banner, IWelcomeLp, IWorkoutPopularityConfig } from '../base/types';
import WorkoutPopularityConfig from '../base/workout-popularity-config.json';
import { BridgeActions } from '../bridge';
import { ChallengeSelectors } from '../challenge';
import { CyclesActions, CyclesSelectors, CyclesTypes } from '../cycles';
import { EvaluationActions } from '../evaluation';
import { ExerciseBaseRoute } from '../exercise/Exercise';
import { GroupAdminActions } from '../groupAdmin';
import { HighscoresActions } from '../highscores';
import { EMigrationFlag, MigrationRoute } from '../migration';
import { SessionActions } from '../session';
import { SubscriptionActions } from '../subscription';
import { SuccessPredictorActions } from '../successPredictor';
import { UnityActions } from '../unity';
import * as AppBridgeActionTypes from './actionTypes';
import { AppActions } from './slice';

export function* waitForLanguage() {
  let language = yield select(LanguageSelectors.getLanguage);
  while (!language) {
    // wait for language update action
    yield take(LanguageActions.updateLanguage);
    language = yield select(LanguageSelectors.getLanguage);
  }
}

export function* handleAppStart() {
  // Init ApiService
  yield call(ApiService.init, ENV.ApiUrl);
  yield call(waitForLanguage);
  const searchParams: URLSearchParams = yield call(getCurrentSearchParams);
  // init firebase
  yield put(
    FirebaseActions.firebaseInitAction(
      ENV.FirebaseConfig.apiKey,
      ENV.FirebaseConfig.projectId,
      ENV.FirebaseConfig.appId,
      ENV.FirebaseConfig.messagingSenderId,
      ENV.FirebaseConfig.measurementId,
      searchParams
    )
  );
  yield take(FirebaseActionTypes.FIREBASE_INITIALIZED);
  yield put(FirebaseActions.firebaseStartAction({ analytics: true, config: true, messaging: true }));
  yield take(FirebaseActionTypes.FIREBASE_READY);
  // start session token restoration
  yield put(
    AuthActions.authRestore({
      getCurrentSearchParams: HistoryService.getCurrentSearchParams,
      exchangeAccessToken: HistoryService.exchangeAccessToken,
      updateSearchParams: HistoryService.updateSearchParams,
      refreshToken: HistoryService.refreshToken,
      redirectLogin: HistoryService.redirectLogin,
      redirectLogout: HistoryService.redirectLogout,
    })
  );
  // wait for auth result
  const authResult = yield take([AuthActions.restoreAuthSuccess, AuthActions.restoreAuthFail]);
  if (authResult.type === AuthActions.restoreAuthFail.type) {
    yield put(AuthActions.redirectToSSO());
    return;
  }
  // add interceptor for requests failed with 401 error
  yield call(addAuthInterceptor);
  // add retry feature to all following calls to api
  yield call(addRetry);
  yield put(Lottie.Actions.initLottieAction(ENV.AssetsBaseUrl));
  // get user id and token
  const userId = yield select(AuthSelectors.getUserId);
  const token = yield select(AuthSelectors.getToken);
  const isPremium = yield select(AuthSelectors.isPremium);
  const email = yield select(AuthSelectors.getUserEmail);

  // set user id for tracking
  yield put(FirebaseActions.setUserAction(userId, email));
  // load experiments feature as soon as possible
  yield call(loadExperimentsFeature);
  // init & load stats to check onboarding status

  yield put(StatsActions.initStats(userId));
  yield take(StatsActions.statsReady);
  // initialize features with user credentials
  yield put(CyclesActions.initCycles(userId, isPremium));
  yield put(HistogramActions.initHistogram());
  yield put(ProgressActions.initProgress(userId));
  yield put(ConfigActions.initConfig(userId));
  yield put(HighscoresActions.initHighscores());
  yield put(SessionActions.initSession(userId));
  yield put(BridgeActions.initBridge(userId));
  yield put(AchievementsActions.initAchievements(userId));
  yield put(EvaluationActions.initEvaluation());
  yield put(SuccessPredictorActions.initSuccessPredictor());
  yield put(AssessmentEvaluationActions.initAssessmentEvaluation());

  yield put(IterableActions.initIterable());
  const isIterableStarted: boolean = yield select(IterableSelectors.isStarted);
  if (!isIterableStarted) {
    yield take(IterableActions.iterableStarted);
  }
  yield put(IterableActions.getMessages());

  // if user is not migrated - redirect to migration
  const migrationStat: number | undefined = yield select(
    StatsSelectors.getStatIntValueById,
    WORKOUT_PROGRESS_MIGRATION
  );
  const migrationFlag: EMigrationFlag = migrationStat ?? 0;
  if (migrationFlag !== EMigrationFlag.MIGRATION_CONFIRMED_BY_APP) {
    yield put(AppActions.appStarted());
    return redirectInternal(MigrationRoute);
  }
  if (isOnPage(MigrationRoute)) {
    // skip other checks if on migration page
    yield put(AppActions.appStarted());
    return;
  }
  yield fork(startBridgeFeature);
  yield fork(trackExperiments);
  yield fork(initMessaging);

  // wait for cycles ready action
  const isCyclesLoading = yield select(CyclesSelectors.isCyclesLoading);
  if (isCyclesLoading) {
    yield take(CyclesActions.cyclesReady);
  }
  // if user didn't complete onboarding, redirect there
  const isOnboardingFinished: boolean = yield select(CyclesSelectors.isOnboardingFinished);
  if (!isOnboardingFinished) {
    // we need to clean cycle and progress cache before redirect
    yield all([call(cleanCyclesCache), call(cleanProgressCache)]);
    const language = yield select(LanguageSelectors.getLanguage);
    return redirectToOnboarding(language, token);
  }
  yield call(preloadLastChallengeProgress);
  yield spawn(markOnboardingAsCompleted);
  yield spawn(initGroupAdmin);
  if (isPremium) {
    // does not make sense to load subscription information if user is not premium
    yield put(SubscriptionActions.initSubscription());
  }
  // start loading progress in background
  yield call(startProgressLoading);
  // set app to started state
  yield put(AppActions.appStarted());
}

export function* startProgressLoading() {
  const isReady: boolean = yield select(ProgressSelectors.isProgressReady);
  if (!isReady) {
    yield take(ProgressActions.progressReady);
  }
  yield put(ProgressActions.loadProgress());
}

export function* cleanCyclesCache() {
  yield put(CyclesActions.cyclesClean());
  yield take(CyclesActions.cleanDone);
}

export function* cleanProgressCache() {
  yield call(waitForFullProgressSaga);
  yield put(ProgressActions.cleanProgress());
  yield take(ProgressActions.cleanProgressDone);
}

export function* initGroupAdmin() {
  const isAdmin = yield select(AuthSelectors.isGroupAdmin);
  if (!isAdmin) {
    // no need to initialize if not group admin
    return;
  }
  const userId = yield select(AuthSelectors.getUserId);
  yield put(GroupAdminActions.initGroup(userId));
}

export function* markOnboardingAsCompleted() {
  const onboardingCompletedStat: unknown = yield select(StatsSelectors.getStatById, '4-12-1');
  if (typeof onboardingCompletedStat !== 'undefined') {
    // onboarding already marked as completed
    return;
  }
  // save stat to backend so that we can identify that user has finished the flow by opening dashboard
  yield put(StatsActions.updateGenericStat('4-12-1', { valueInt: 1 }));
  yield put(StatsActions.saveStatsQueue());
}

export function* loadExperimentsFeature() {
  const experiments = [5121, 5722, 7428];
  yield put(ExperimentsActions.initExperiments({ experiments }));
  yield take(ExperimentsActions.experimentsReady);
}

export function* startBridgeFeature() {
  // Ensure that firebse is ready
  yield call(waitForFirebaseReadySaga);
  // request bridge actions and detect bridge at the end
  yield put(BridgeActions.requestBridge());
}

export function* trackExperiments() {
  // Ensure that experiments feature is ready
  yield call(waitForExperimentsFeature);
  // Ensure that firebase is ready
  yield call(waitForFirebaseReadySaga);
  const workoutPopularity: IWorkoutPopularityConfig = yield call(
    RemoteConfigService.getObjectConfigValue,
    ENV.RemoteConfigWorkoutPopularity,
    WorkoutPopularityConfig
  );
  yield put(FirebaseActions.setUserPropertyAction('experiment_5475', workoutPopularity.variant));
  const day0BannerConfig: IDay0Banner = yield call(
    RemoteConfigService.getObjectConfigValue,
    ENV.RemoteConfigDay0Banner,
    DEFAULT_DAY0_BANNER
  );
  yield put(FirebaseActions.setUserPropertyAction('experiment_5141', day0BannerConfig.variant));
  const moodTrackerVariant: string | undefined = yield select(ExperimentsSelectors.getExperimentVariant, 7428);
  yield put(FirebaseActions.setUserPropertyAction('experiment_7428', moodTrackerVariant));
  const voicedNeuroboostersVariant: string | undefined = yield select(ExperimentsSelectors.getExperimentVariant, 5722);
  yield put(FirebaseActions.setUserPropertyAction('experiment_5722', voicedNeuroboostersVariant));
  const welcomeLpConfig: IWelcomeLp = yield call(
    RemoteConfigService.getObjectConfigValue,
    ENV.RemoteConfigWelcomeLp,
    DEFAULT_WELCOME_LP
  );
  yield put(FirebaseActions.setUserPropertyAction('experiment_5757', welcomeLpConfig.variant));
  const challengeVariant: string | undefined = yield select(ExperimentsSelectors.getExperimentVariant, 5121);
  yield put(FirebaseActions.setUserPropertyAction('experiment_5121', challengeVariant));
}

export function* waitForStats() {
  const statsLoading: boolean = yield select(StatsSelectors.isStatsLoading);
  if (!statsLoading) {
    return;
  }
  yield take(StatsActions.statsReady);
}

export function* initMessaging() {
  // Ensure that stats are ready
  yield call(waitForStats);
  const messagingEnabled: boolean = yield select(StatsSelectors.getStatBooleanValueById, TRAINING_REMINDER);
  if (!messagingEnabled) {
    return;
  }

  yield put(FirebaseActions.enableMessagingAction(ENV.VapId));
}

export function* preloadLastChallengeProgress() {
  yield call(waitForExperimentsFeature);
  yield call(waitForFirebaseReadySaga);

  const challengeActive = yield select(ChallengeSelectors.isChallengeActive);

  if (!challengeActive) {
    return;
  }

  const challengeWorkoutIds: number[] | undefined = yield select(ChallengeSelectors.getChallengeWorkoutIds);
  if (!challengeWorkoutIds || !challengeWorkoutIds.length) {
    return;
  }

  yield put(ProgressActions.loadLastProgress(challengeWorkoutIds));
  yield take(ProgressActions.updateWorkoutLastProgress);
}

export function* handleBridgeActionRedirectSaga({ payload }: AppBridgeActionTypes.IRedirectAction) {
  const { target, url } = payload;
  if (target) {
    yield call(openExternalUrl, url);
    return;
  }
  yield call(redirectExternal, url);
}

export function* handleBridgeActionRouteSaga({ payload }: AppBridgeActionTypes.IRouteAction) {
  yield call(redirectInternal, payload);
}

export function* handleBridgeActionRedirectCheckoutSaga({ payload }: AppBridgeActionTypes.IRedirectCheckoutAction) {
  const { coupon, productId } = payload;
  const lang = yield select(LanguageSelectors.getLanguage);
  const session = yield select(AuthSelectors.getToken);
  yield call(redirectToCheckout, productId, lang, session, coupon);
}

export function* handleBridgeActionRedirectExerciseSaga({ payload }: PayloadAction<string>) {
  const exerciseId = payload;
  const exerciseUrl = `${ExerciseBaseRoute}/${exerciseId.toLowerCase()}`;
  yield call(redirectInternal, exerciseUrl);
}

export function* handleRedirectSubscribeSaga({ payload }: PayloadAction<string | undefined>) {
  const lang = yield select(LanguageSelectors.getLanguage);
  const session = yield select(AuthSelectors.getToken);
  yield call(redirectToSubscribeWithCoupon, payload, lang, session);
}

export function* handleLogoutSaga() {
  // clean cycles and progress feature DBs
  // progress feature uses shared cache - it will be cleared too
  yield all([call(cleanCyclesCache), call(cleanProgressCache)]);
  // do actual logout
  yield put(AuthActions.logout());
}

export function* handleJoinChallengeSaga() {
  // Track join challenge
  yield put(FirebaseActions.analyticsEventAction({ eventName: 'challenge_join_button' }));
  yield put(AppActions.startChallenge());
}

export function* handleStartChallengeSaga() {
  // detect what is todays exercise in the challenge
  const workoutId = yield select(ChallengeSelectors.getTodaysChallengeWorkoutId);
  // Track start challenge
  yield put(FirebaseActions.analyticsEventAction({ eventName: 'challenge_start_button' }));

  // redirect to exercise page
  const exerciseId = yield select(ConfigSelectors.getWorkoutExerciseId, workoutId);
  yield put(AppActions.redirectExercise(exerciseId));
}

export function* restartUnityOnLanguageChangeSaga() {
  yield take(LanguageActions.updateLanguage);
  // we restart unity on every language change

  yield put(UnityActions.unityRestart());
}

export function* handleGlobalAchievementDetectionSaga({
  payload: { currentCycleId, currentSessionId, cycleLength, finishedCycles, finishedSessions, numberWorkouts },
}: PayloadAction<CyclesTypes.IUpdateStatsPayload>) {
  // update global stats of achievements
  // currently we need only last two: numberWorkouts and finishedSessions
  // in future when feature will be completed we can consider to remove unused
  yield put(
    AchievementsActions.achievementsGlobalStats(
      currentCycleId,
      currentSessionId,
      cycleLength,
      finishedCycles,
      finishedSessions,
      numberWorkouts
    )
  );
}

export function* trackLanguageInFirebase() {
  yield call(waitForFirebaseReadySaga);
  const language = yield select(LanguageSelectors.getLanguage);
  yield put(FirebaseActions.setUserPropertyAction('website_language', language));
}

export default function* watchApp() {
  yield all([
    takeEvery(AppActions.appStart, handleAppStart),
    takeEvery(AppBridgeActionTypes.ROUTE, handleBridgeActionRouteSaga),
    takeEvery(AppBridgeActionTypes.REDIRECT_TO, handleBridgeActionRedirectSaga),
    takeEvery(AppBridgeActionTypes.REDIRECT_CHECKOUT, handleBridgeActionRedirectCheckoutSaga),
    takeEvery(AppActions.redirectExercise, handleBridgeActionRedirectExerciseSaga),
    takeEvery(AppActions.redirectSubscribe, handleRedirectSubscribeSaga),
    takeLeading(AppActions.appLogout, handleLogoutSaga),
    takeLeading(AppActions.joinChallenge, handleJoinChallengeSaga),
    takeLeading(AppActions.startChallenge, handleStartChallengeSaga),
    // we are using latest effect while we need the latest effective user language
    takeLatest(LanguageActions.updateLanguage, trackLanguageInFirebase),
    takeLatest(LanguageActions.changeLanguage, restartUnityOnLanguageChangeSaga),
    takeEvery(CyclesActions.updateStats, handleGlobalAchievementDetectionSaga),
  ]);
}
